export type ProviderRateLimitRetrySignal = {
  errorCode: string;
  retryAfterMs: number | null;
  status: number | null;
};

export type ProviderRateLimitRetryableErrorOptions = {
  errorCode?: string | null;
  retryAfterMs?: number | null;
  status?: number | null;
};

export class ProviderRateLimitRetryableError extends Error {
  readonly providerRateLimited = true;
  readonly errorCode: string;
  readonly retryAfterMs: number | null;
  readonly status: number | null;

  constructor(message: string, options: ProviderRateLimitRetryableErrorOptions = {}) {
    super(message);
    this.name = "ProviderRateLimitRetryableError";
    this.errorCode = options.errorCode?.trim() || "provider_rate_limited";
    this.retryAfterMs = normalizeRetryAfterMs(options.retryAfterMs);
    this.status = normalizeStatus(options.status);
  }
}

export function parseRetryAfterHeaderMs(value: string | null, nowMs = Date.now()): number | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;

  const seconds = Number(trimmed);
  if (Number.isFinite(seconds) && seconds >= 0) {
    return Math.ceil(seconds * 1000);
  }

  const dateMs = Date.parse(trimmed);
  if (!Number.isFinite(dateMs)) return null;
  return Math.max(0, dateMs - nowMs);
}

export function providerRateLimitRetrySignal(error: unknown): ProviderRateLimitRetrySignal | null {
  if (error instanceof ProviderRateLimitRetryableError) {
    return {
      errorCode: error.errorCode,
      retryAfterMs: error.retryAfterMs,
      status: error.status,
    };
  }

  if (!error || typeof error !== "object") return null;
  const record = error as {
    providerRateLimited?: unknown;
    errorCode?: unknown;
    retryAfterMs?: unknown;
    status?: unknown;
  };
  if (record.providerRateLimited !== true) return null;

  return {
    errorCode: typeof record.errorCode === "string" && record.errorCode.trim() ? record.errorCode : "provider_rate_limited",
    retryAfterMs: normalizeRetryAfterMs(record.retryAfterMs),
    status: normalizeStatus(record.status),
  };
}

function normalizeRetryAfterMs(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? Math.ceil(value) : null;
}

function normalizeStatus(value: unknown): number | null {
  return typeof value === "number" && Number.isInteger(value) && value >= 100 && value <= 599 ? value : null;
}
